import React from 'react'
import { setKeyword, setLoading } from '../store/searchSlice'
import { useSelector, useDispatch } from 'react-redux'
import ProductTile from '../product-tiles/ProductTile.jsx'

import { cacheDom } from '../../utils/QuerySelectors.js'

const dom = {}
const qs = {
  searchPanel: 'data-search-panel',
  searchResults: 'data-search-results',
}
const qsAll = {}

const InlineSearch = () => {
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.search)
  const keyThreshold = 500 // If there are no key events within 500ms, then get results feed.
  cacheDom(dom, qs, qsAll)

  // Function to announce search results to screen readers
  const announceSearchResults = (message) => {
    const announcement = document.getElementById('search-results-announcement')
    if (announcement) {
      announcement.textContent = message
    }
  }

  const searchString = (e) => {
    clearTimeout(window.keyTimer)

    window.keyTimer = setTimeout(() => {
      const searchTerm = e.target.value.trim()
      if (searchTerm.length != 0) {
        const url = `/search?q=${encodeURIComponent(searchTerm)}&type=product`
        dispatch(setLoading(true))
        announceSearchResults('Searching...')

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html')
            const productGrid = html.querySelector('.main-collection')
            const productCount = productGrid ? productGrid.querySelectorAll('[product-tile]').length : 0

            document.querySelector(`[${qs.searchResults}]`).innerHTML =
              `<div class="inline-search-results--main w-full">${productGrid ? productGrid.innerHTML : ''}</div>`

            setTimeout(() => {
              dispatch(setLoading(false))
              window.setupProductTiles(document.querySelectorAll('[product-tile="false"]'))

              // Announce search results
              if (productCount > 0) {
                announceSearchResults(`${productCount} products found for "${searchTerm}"`)
              } else {
                announceSearchResults(`No products found for "${searchTerm}"`)
              }
            }, 50)
            window.updateSearchRecommend(dom.searchPanel)

            fetch(`/search/suggest.json?q=${searchTerm}&resources[type]=collection&resources[limit]=10`)
              .then((response) => response.json())
              .then((data) => {
                console.log(data)
                if (data.resources.results.collections.length > 0) {
                  const collectionCount = data.resources.results.collections.filter(
                    (col) => !col.title.includes('_linked'),
                  ).length
                  document.querySelector(`[${qs.searchResults}]`).innerHTML =
                    `<ul class="inline-search-results--collections flex flex-col gap-3 lg:w-[13.4375rem]">
                    <li class='font-tinos text-sm leading-4 mb-3' role='heading' aria-level='2'>Suggestions</li>
                    ${data.resources.results.collections
                      .map((col) => {
                        return col.title.includes('_linked')
                          ? ''
                          : `<li>
                          <a class="capitalize text-sm leading-4 no-underline hover:underline text-black hover:text-black focus:underline" href="/collections/${col.handle}">
                            ${col.title.toLowerCase().replace(searchTerm.toLowerCase(), '<span class="font-bold">' + searchTerm + '</span>')}
                          </a>
                        </li>`
                      })
                      .join('')}
                  </ul>` + document.querySelector(`[${qs.searchResults}]`).innerHTML

                  // Announce collection suggestions
                  if (collectionCount > 0) {
                    announceSearchResults(`${collectionCount} collection suggestions available`)
                  }
                }
              })
              .catch((error) => {
                console.error('Search error:', error)
                announceSearchResults('Search error occurred')
              })
          })
          .catch((error) => {
            console.error('Search error:', error)
            dispatch(setLoading(false))
            announceSearchResults('Search error occurred')
          })
      } else {
        dispatch(setKeyword(''))
        announceSearchResults('')
      }
    }, keyThreshold)
  }

  return (
    <div className="h-full flex flex-col overflow-auto">
      <form
        className="flex items-center justify-between gap-4 border-b border-second lg:mt-4 pb-3 mt-6 lg:mt-0 mb-6 lg:mb-10 relative group"
        action="/search"
        method="get"
      >
        <label
          className="absolute z-1 left-0 top-0 cursor-pointer"
          htmlFor="inlineSearchInput"
          dangerouslySetInnerHTML={{ __html: window.globalIcons['icon-search'] }}
          aria-label="Search an item"
        />
        <input
          className="pl-8 appearance-none bg-transparent text-base text-black leading-6 placeholder:text-black grow focus:placeholder:text-transparent"
          type="text"
          name="q"
          id="inlineSearchInput"
          placeholder="Search"
          autoFocus={true}
          onKeyUp={(e) => searchString(e)}
          data-search-box
        />
        <input name="type" type="hidden" value="product" />
        {loading && <span className="search-loader absolute right-8 top-1" />}
        <button
          className="text-black focus:outline"
          dangerouslySetInnerHTML={{ __html: window.globalIcons['icon-arrow-right'] }}
          aria-label="Submit"
        />
      </form>
      {/* ARIA live region for search results announcements (kept outside data-search-results so innerHTML rewrites don't remove it) */}
      <div
        id="search-results-announcement"
        className="visually-hidden"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      ></div>
      <div className="inline-search-results flex gap-y-6 gap-x-3 flex-col lg:flex-row" data-search-results>
        <ul className="flex flex-col gap-3 lg:w-[13.4375rem]" data-search-presets>
          <li className="font-tinos text-sm leading-4 mb-3" role="heading" aria-level="2">
            Suggestions
          </li>
          {window.searchPresets.map((preset, index) => (
            <li key={`search-preset-${index}`}>
              <a
                className="text-sm leading-4 no-underline hover:underline text-black hover:text-black focus:underline"
                href={preset.url}
              >
                {preset.title}
              </a>
            </li>
          ))}
        </ul>
        {window.searchRecommend && window.searchRecommend.products.length > 0 && (
          <div className="lg:w-[calc(100%-14.1875rem)]">
            {window.searchRecommend.title && (
              <div className="font-tinos text-sm leading-4 mb-3">{window.searchRecommend.title}</div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-y-4 gap-x-3 lg:gap-x-4">
              {window.searchRecommend.products.map((product, index) => (
                <div
                  className={`h-full ${index > 4 ? 'lg:hidden' : ''}`}
                  product-tile="true"
                  key={`search-recommend-${product.id}-${index}`}
                >
                  <ProductTile props={product} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default InlineSearch
